import { removeUndefinedValues } from "../utils/firestore";
import { CollectionPaths } from "@order-up/common";
import { BusinessType, IBusinessTypeData } from "@order-up/models";
import {
  DocumentWithId,
  FirestoreRepository,
} from "../interfaces/FirestoreRepository";
import { createLogger } from "../utils/logger";
import { counterService } from "./counters";
import { createApiClient } from "./apiClient";

const logger = createLogger("BusinessTypeService");

const apiClient = createApiClient("/api/v1");

const businessTypesPath = () => CollectionPaths.businessTypes();

// Seeding is a server-owned operation — the curated list lives in
// @order-up/core and is written via the admin SDK behind requireAdmin.
const businessTypesApi = createApiClient("/api/v1/business-types");

/**
 * Platform-level collection.
 *
 * Reads go through the session-authenticated API endpoint instead of the
 * Firebase client SDK. The Firestore rule `allow read: if request.auth != null`
 * fails whenever the Firebase client SDK hasn't finished signing in (e.g. on a
 * page-refresh race before TokenHandler reconciles), so we route reads through
 * the API — same pattern as `getTenantsByUserId`.
 *
 * Writes still go via the admin-app Firebase client SDK (admins are signed in).
 */

const getBusinessTypes = async (): Promise<
  DocumentWithId<BusinessType>[]
> => {
  logger.debug("Getting business types");

  try {
    const response = (await apiClient.get("/business-types")) as any;
    const data = response?.data || response;
    const rawTypes = (data?.businessTypes || []) as Array<Record<string, unknown> & { docId: string }>;
    return rawTypes.map((raw) => {
      const { docId, ...rest } = raw;
      const model = BusinessType.fromJSON(rest);
      return Object.assign(model, { docId }) as DocumentWithId<BusinessType>;
    });
  } catch (error) {
    logger.error("Error fetching business types", error);
    throw error;
  }
};

const createBusinessType = async (
  data: Omit<
    IBusinessTypeData,
    "businessTypeId" | "guid" | "createdAt" | "updatedAt"
  > & {
    name: string;
  },
): Promise<DocumentWithId<BusinessType>> => {
  logger.debug("Creating business type", { name: data.name });

  const { businessTypeId, guid } =
    await counterService.generateBusinessTypeId();

  const payload = removeUndefinedValues({
    businessTypeId,
    guid,
    name: data.name,
    isActive: data.isActive ?? true,
    position: data.position ?? 0,
    createdAt: FirestoreRepository.getServerTimestamp(),
    updatedAt: FirestoreRepository.getServerTimestamp(),
  });

  const collectionPath = businessTypesPath();
  const repo = new FirestoreRepository<BusinessType>(
    collectionPath,
    BusinessType,
  );
  const created = await repo.create(payload as any);
  const out = await repo.getById(created.docId);
  if (!out) throw new Error("Failed to retrieve created business type");
  return out;
};

// Mutations resolve the target by `guid` (the model's stable, unique external
// reference) rather than `businessTypeId`. The numeric counter ID is not
// guaranteed unique across counter resets/re-seeds, so a `businessTypeId`
// lookup could mutate the wrong document; `guid` is a collision-proof UUID.
const updateBusinessType = async (
  guid: string,
  data: Partial<
    Omit<IBusinessTypeData, "businessTypeId" | "guid" | "createdAt">
  >,
): Promise<DocumentWithId<BusinessType>> => {
  logger.debug("Updating business type", { guid });

  const collectionPath = businessTypesPath();
  const repo = new FirestoreRepository<BusinessType>(
    collectionPath,
    BusinessType,
  );
  const results = await repo.find([
    {
      field: "guid",
      operator: "==",
      value: guid,
    },
  ]);
  if (results.length === 0)
    throw new Error(`Business type not found: ${guid}`);

  const existingDocId = results[0].docId;
  const updatePayload = removeUndefinedValues({
    ...data,
    updatedAt: FirestoreRepository.getServerTimestamp(),
  });

  await repo.updateById(existingDocId, updatePayload);
  const updated = await repo.getById(existingDocId);
  if (!updated)
    throw new Error(
      `Failed to retrieve updated business type ${guid}`,
    );
  return updated;
};

const deleteBusinessType = async (
  guid: string,
): Promise<void> => {
  logger.debug("Deleting business type", { guid });

  const collectionPath = businessTypesPath();
  const repo = new FirestoreRepository<BusinessType>(
    collectionPath,
    BusinessType,
  );
  const results = await repo.find([
    {
      field: "guid",
      operator: "==",
      value: guid,
    },
  ]);
  if (results.length === 0)
    throw new Error(`Business type not found: ${guid}`);
  await repo.deleteById(results[0].docId);
};

/**
 * Result shape returned by the `POST /api/v1/business-types/seed` endpoint.
 */
export interface ISeedBusinessTypesResult {
  typesWritten: number;
  typesUpdated: number;
  typesSkipped: number;
  subCategoriesWritten: number;
  subCategoriesUpdated: number;
  subCategoriesSkipped: number;
}

/**
 * Ask the server to seed the curated business types and sub-categories.
 * Admin-only. The curated data lives server-side in @order-up/core; the
 * browser sends an empty body.
 *
 * Idempotent, and it RECONCILES: an existing row keeps its identity and its
 * `isActive` flag but has its presentation fields brought back in line with
 * the curated seed. Re-running after a curated edit is how a change reaches
 * an environment.
 */
const seedBusinessTypes = async (): Promise<ISeedBusinessTypesResult> => {
  logger.info("Seeding curated business types");

  try {
    const response = (await businessTypesApi.post("/seed", {})) as {
      success: boolean;
      data: ISeedBusinessTypesResult;
    };
    if (!response?.success || !response.data) {
      throw new Error("Invalid seed response from API");
    }
    logger.info("Seed complete", { ...response.data });
    return response.data;
  } catch (error) {
    logger.error("Error seeding business types", {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

const subscribeToBusinessTypes = (
  callback: (types: DocumentWithId<BusinessType>[]) => void,
  onError?: (error: Error) => void,
): (() => void) => {
  const collectionPath = businessTypesPath();
  const repo = new FirestoreRepository<BusinessType>(
    collectionPath,
    BusinessType,
  );
  return repo.subscribeToCollection([], [], callback, onError);
};

export const businessTypeService = {
  getBusinessTypes,
  createBusinessType,
  updateBusinessType,
  deleteBusinessType,
  seedBusinessTypes,
  subscribeToBusinessTypes,
};
